import React, { useContext, useEffect } from 'react';
import { Route, ScrollToTop, Routes } from '@thd-olt-component-react/router';
import { AppContext } from '@thd-nucleus/app-render';
import { ThemeProvider } from '@material-ui/core';
import { Error } from './pages';
import { getRoutes } from './routes.js';
import { THDTheme } from './theme';
import './styles/app.scss';
import { redirectToSignInEncoded, redirectTo } from './utils/general';
import { isLoggedIn } from './utils/user';

interface AppProps {
  customerType: string;
  customerRole: string;
  userId: string;
  svocId: string;
  serverPages: unknown;
}

interface ProtectedRouteProps {
  children: React.ReactElement;
  customerType: string;
  customerRole: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  customerType,
  customerRole,
}) => {
  // client-side only check -- do not use for conditional rendering
  const isUserLoggedIn = isLoggedIn();
  const isB2B = customerType?.toUpperCase() === 'B2B';
  const isAdmin = customerRole?.toUpperCase() === 'ADMIN';
  const shouldRedirect = !isB2B || !isAdmin;

  useEffect(() => {
    // due to the fact shouldRedirect will also be true for non-logged in users, perform this check first
    if (!isUserLoggedIn) {
      redirectToSignInEncoded();
      return;
    }
    if (shouldRedirect) {
      redirectTo('/myaccount/dashboard');
    }
  }, [shouldRedirect]);

  if (shouldRedirect) {
    return null;
  }

  return children;
};

export const App = ({
  customerType,
  customerRole,
  userId,
  svocId,
  serverPages,
}: AppProps) => {
  const { instance } = useContext(AppContext);
  const { currentRouteName, currentRouteComponent } = instance.router;
  const routes = getRoutes(
    currentRouteName,
    currentRouteComponent,
    serverPages,
  );

  useEffect(() => {
    document.title = 'The Home Depot';
  }, []);

  return (
    <>
      <ThemeProvider theme={THDTheme}>
        <ScrollToTop />
        <Routes>
          <Route
            path={routes.registration.path}
            element={(
              <ProtectedRoute
                customerType={customerType}
                customerRole={customerRole}
              >
                <routes.registration.component
                  userId={userId}
                  svocId={svocId}
                />
              </ProtectedRoute>
            )}
          />
          <Route
            path={routes.pricingBenefits.path}
            element={(
              <ProtectedRoute
                customerType={customerType}
                customerRole={customerRole}
              >
                <routes.pricingBenefits.component />
              </ProtectedRoute>
            )}
          />
          <Route path="/error" element={<Error />} />
          <Route element={<Error />} />
        </Routes>
      </ThemeProvider>
    </>
  );
};
